<template>
  <div class="min-h-full bg-slate-50 pb-24">
    <div class="max-w-3xl mx-auto px-4 py-6 space-y-4">

      <!-- 标题控制区卡片 -->
      <div class="bg-white rounded-lg shadow-sm p-6">
        <!-- 顶部：标题 + 搜索 + 排序 -->
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-2">
            <IconSword class="w-5 h-5 text-[#F2C94C]" />
            <h2 class="text-2xl font-black text-slate-900">实战演练</h2>
          </div>

          <div class="flex items-center gap-3">
            <!-- 排序按钮 -->
            <div class="flex items-center gap-2">
              <span class="text-xs text-slate-400">排序：</span>
              <button
                @click="handleSort('practiceCount')"
                :class="`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs transition-all ${sortBy === 'practiceCount' ? 'bg-amber-100 text-amber-600 font-medium' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`"
              >
                <IconUsers class="w-3 h-3" />
                练习人数
                <span v-if="sortBy === 'practiceCount'">{{ sortOrder === 'desc' ? '↓' : '↑' }}</span>
              </button>
              <button
                @click="handleSort('accuracyRate')"
                :class="`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs transition-all ${sortBy === 'accuracyRate' ? 'bg-amber-100 text-amber-600 font-medium' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`"
              >
                <IconTarget class="w-3 h-3" />
                正确率
                <span v-if="sortBy === 'accuracyRate'">{{ sortOrder === 'desc' ? '↓' : '↑' }}</span>
              </button>
              <button
                v-if="sortBy !== 'default'"
                @click="sortBy = 'default'"
                class="text-xs text-slate-400 hover:text-slate-600 underline"
              >
                重置
              </button>
            </div>

            <!-- 搜索框 -->
            <div class="relative">
              <input
                type="text"
                placeholder="搜索题目..."
                v-model="searchQuery"
                class="w-48 px-4 py-2 pl-10 bg-slate-50 border border-slate-200 rounded-full text-sm focus:outline-none focus:border-[#F2C94C] focus:ring-2 focus:ring-[#F2C94C]/20 transition-all"
              />
              <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        <!-- 金色分隔线 -->
        <div class="w-12 h-0.5 bg-[#F2C94C] mb-4"></div>

        <!-- 分类标签 -->
        <div class="flex gap-2">
          <button
            v-for="tab in practiceTabs"
            :key="tab"
            @click="activeTab = tab"
            :class="`px-4 py-2 text-sm rounded-full border transition-all ${activeTab === tab ? 'bg-[#F2C94C] text-white border-[#F2C94C] font-medium' : 'bg-white text-slate-600 border-slate-200 hover:border-[#F2C94C] hover:text-[#F2C94C]'}`"
          >
            {{ tab }}
          </button>
        </div>
      </div>

      <!-- 场景卡片列表 -->
      <div class="space-y-3">
        <div
          v-for="scenario in sortedScenarios"
          :key="scenario.id"
          class="bg-white rounded-lg shadow-sm p-6 flex items-center justify-between border border-slate-100"
        >
          <div class="flex-1">
            <!-- 分类标签 - 统一风格 -->
            <div class="flex items-center gap-2 mb-2">
              <span class="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded">
                {{ getScenarioCategory(scenario.description) }}
              </span>
            </div>

            <!-- 标题 -->
            <h4 class="text-slate-900 text-base font-bold mb-3">
              {{ (scenario.description.split('】')[0].replace('【', '')) || scenario.description }}
            </h4>

            <!-- 元信息 -->
            <div class="flex items-center gap-4">
              <div class="flex items-center gap-1.5 text-xs text-slate-400">
                <IconUsers class="w-3.5 h-3.5" />
                <span>{{ (scenario.practiceCount || 0).toLocaleString() }}人已练</span>
              </div>
              <div class="flex items-center gap-1.5 text-xs text-slate-400">
                <IconTarget class="w-3.5 h-3.5" />
                <span>正确率 {{ scenario.accuracyRate || 0 }}%</span>
              </div>
            </div>
          </div>

          <!-- 开始按钮 -->
          <button
            @click="selectedScenario = scenario"
            class="px-6 py-2.5 bg-[#F2C94C] text-white font-medium rounded-full text-sm hover:bg-[#E5B73B] transition-colors shadow-sm"
          >
            开始演练
          </button>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="sortedScenarios.length === 0" class="text-center py-12">
        <div class="text-slate-400 text-sm">暂无匹配的场景</div>
      </div>
    </div>

    <!-- 实战演练弹窗 -->
    <SimulationEngine
      v-if="selectedScenario"
      :scenario="selectedScenario"
      @exit="selectedScenario = null"
      @next="handleNextScenario"
    />
  </div>
</template>

<script>
import { IconSword, IconUsers, IconTarget } from '../components/icons'
import { SCENARIO_DATA } from '../data'
import {
  TOPIC_TO_SCENARIO_MAP,
  PRACTICE_TABS,
  getScenarioCategory,
} from '../constants/scenarios'
import SimulationEngine from '../components/practice/SimulationEngine.vue'

export default {
  name: 'PracticePage',

  components: {
    IconSword,
    IconUsers,
    IconTarget,
    SimulationEngine,
  },

  props: {
    targetTopicId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      selectedScenario: null,
      activeTab: '全部',
      sortBy: 'default',
      sortOrder: 'desc',
      searchQuery: '',
      practiceTabs: PRACTICE_TABS,
    }
  },

  computed: {
    storeTargetTopicId() {
      return this.$store.state.targetTopicId
    },

    effectiveTargetTopicId() {
      return this.targetTopicId ?? this.storeTargetTopicId
    },

    sortedScenarios() {
      let scenarios = Object.values(SCENARIO_DATA)
        .filter(scenario => {
          // 搜索过滤
          if (this.searchQuery.trim()) {
            const query = this.searchQuery.toLowerCase()
            return scenario.description.toLowerCase().includes(query) ||
              getScenarioCategory(scenario.description).toLowerCase().includes(query)
          }
          // 标签过滤
          if (this.activeTab === '全部') return true
          if (this.activeTab === '人才留存') return scenario.description.includes('离职') || scenario.description.includes('留存')
          if (this.activeTab === '绩效管理') return scenario.description.includes('绩效') || scenario.description.includes('目标')
          if (this.activeTab === '跨部门沟通') return scenario.description.includes('跨部门') || scenario.description.includes('协同')
          return true
        })

      if (this.sortBy === 'default') return scenarios

      return scenarios.sort((a, b) => {
        const aValue = this.sortBy === 'practiceCount' ? (a.practiceCount || 0) : (a.accuracyRate || 0)
        const bValue = this.sortBy === 'practiceCount' ? (b.practiceCount || 0) : (b.accuracyRate || 0)
        return this.sortOrder === 'desc' ? bValue - aValue : aValue - bValue
      })
    },
  },

  watch: {
    effectiveTargetTopicId: {
      immediate: true,
      handler(val) {
        if (val && !this.selectedScenario) {
          const scenarioId = TOPIC_TO_SCENARIO_MAP[val]
          if (scenarioId && SCENARIO_DATA[scenarioId]) {
            this.selectedScenario = SCENARIO_DATA[scenarioId]
            this.$store.commit('SET_TARGET_TOPIC_ID', null)
          }
        }
      },
    },
  },

  methods: {
    getScenarioCategory,

    handleSort(type) {
      if (this.sortBy === type) {
        this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc'
      } else {
        this.sortBy = type
        this.sortOrder = 'desc'
      }
    },

    handlePracticeComplete(scenario, selectedOption, isCorrect, timeSpent) {
      const newRecord = {
        id: `practice-${Date.now()}`,
        scenarioId: scenario.id,
        scenarioTitle: scenario.description,
        category: getScenarioCategory(scenario.description),
        selectedOption,
        isCorrect,
        impact: scenario.options.find(o => o.id === selectedOption)?.impact || { morale: 0, efficiency: 0, retention: 0 },
        timestamp: Date.now(),
        timeSpent,
      }
      const updated = [newRecord, ...this.$store.state.practiceRecords].slice(0, 100)
      this.$store.commit('SET_PRACTICE_RECORDS', updated)
    },

    handleNextScenario() {
      // 找到下一个场景
      const scenarios = Object.values(SCENARIO_DATA)
      const currentIndex = scenarios.findIndex(s => s.id === this.selectedScenario.id)
      const nextScenario = scenarios[currentIndex + 1]
      if (nextScenario) {
        this.selectedScenario = nextScenario
      } else {
        this.selectedScenario = null
      }
    },
  },
}
</script>
